const VERDICT_COLORS = Object.freeze({
  ACCEPT: "#4ade80",
  CAUTION: "#fbbf24",
  REJECT: "#fb7185",
});

export function normalizedCodexInterval(value) {
  const interval = String(value || "").replace(/^_/, "").toUpperCase();
  if (interval === "15" || interval === "15MIN" || interval === "15M") return "15M";
  if (interval === "240" || interval === "4HR" || interval === "4H") return "4H";
  if (interval === "1440" || interval === "1DAY" || interval === "1D" || interval === "D") return "1D";
  return interval;
}

export function collectCodexEvaluations(latest, history = []) {
  const byAttempt = new Map();
  [...(Array.isArray(history) ? history : []), latest].filter(Boolean).forEach((evaluation, index) => {
    const key = evaluation.attemptId || `${evaluation.evaluatedAt || 0}-${index}`;
    byAttempt.set(key, evaluation);
  });
  return [...byAttempt.values()].sort((left, right) => Number(left.evaluatedAt || 0) - Number(right.evaluatedAt || 0));
}

export function codexEvaluationTimestamp(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return numeric < 10_000_000_000 ? numeric * 1_000 : numeric;
}

export function formatCodexMarkerTime(value) {
  const timestamp = codexEvaluationTimestamp(value);
  if (timestamp == null) return "unknown time";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    hour12: false, timeZone: "UTC",
  }).format(new Date(timestamp)).replace(",", "");
}

function evaluatedPrice(evaluation, interval, eventPrice) {
  const timeframes = Array.isArray(evaluation?.details?.timeframes) ? evaluation.details.timeframes : [];
  const normalized = normalizedCodexInterval(interval);
  const matched = timeframes.find((item) => normalizedCodexInterval(item?.interval) === normalized);
  const candidates = [matched?.close, ...timeframes.map((item) => item?.close), eventPrice];
  for (const candidate of candidates) {
    const price = Number(candidate);
    if (Number.isFinite(price) && price > 0) return price;
  }
  return null;
}

export function buildCodexEvaluationMarkers({ latest, history = [], interval, eventPrice }) {
  return collectCodexEvaluations(latest, history).flatMap((evaluation) => {
    const timestamp = codexEvaluationTimestamp(evaluation.evaluatedAt);
    const price = evaluatedPrice(evaluation, interval, eventPrice);
    if (timestamp == null || price == null) return [];
    return [{
      key: evaluation.attemptId || `${timestamp}-${evaluation.verdict || "UNKNOWN"}`,
      time: Math.floor(timestamp / 1_000),
      price,
      label: `Codex${evaluation.responseMode === "REFERENCE_V1" ? " Reference" : ""} · ${formatCodexMarkerTime(timestamp)}`,
      color: VERDICT_COLORS[evaluation.verdict] || "#93c5fd",
      evaluation,
    }];
  });
}

export function buildSharedEvaluationMarkers({ records = [], interval, eventPrice }) {
  return (Array.isArray(records) ? records : []).flatMap((record) => {
    const evaluation = record?.result;
    const timestamp = codexEvaluationTimestamp(record?.completedAt || record?.evaluatedAt);
    const anchorPrice = evaluatedPrice(evaluation, interval, record?.inputDiagnostics?.currentPrice ?? eventPrice);
    if (!evaluation || timestamp == null || anchorPrice == null) return [];
    const provider = record.provider === "CUSTOM_AI" ? "Custom AI"
      : evaluation.responseMode === "REFERENCE_V1" ? "Codex Reference" : "Codex";
    const price = record.provider === "CUSTOM_AI" ? anchorPrice * 1.0025 : anchorPrice * 0.9975;
    return [{
      key: `shared-${record.id}`,
      time: Math.floor(timestamp / 1_000),
      price,
      label: `${provider} · ${formatCodexMarkerTime(timestamp)}`,
      color: record.provider === "CUSTOM_AI"
        ? "#22d3ee" : (VERDICT_COLORS[evaluation.verdict] || "#93c5fd"),
      evaluation: {
        ...evaluation,
        evaluatedAt: evaluation.evaluatedAt || timestamp,
        _sharedRecord: record,
      },
    }];
  });
}

export { VERDICT_COLORS };
